import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileCheck, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

const PODPage = () => {
  const [pods, setPods] = useState([]);
  const [filteredPods, setFilteredPods] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPOD, setNewPOD] = useState({
    delivery_number: '',
    recipient_name: '',
    delivery_timestamp: new Date().toISOString(),
    delivery_notes: '',
    captured_by: 'USER'
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchPODs = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/pod/pods`);
      const data = await response.json();
      setPods(data);
    } catch (error) {
      toast.error('Failed to load PODs');
    }
  }, [backendUrl]);

  const filterPODs = useCallback(() => {
    let filtered = pods;
    if (searchTerm) {
      filtered = filtered.filter(pod => 
        pod.delivery_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pod.pod_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredPods(filtered);
  }, [pods, searchTerm]);

  useEffect(() => {
    fetchPODs();
  }, [fetchPODs]);

  useEffect(() => {
    filterPODs();
  }, [filterPODs]);

  const handleCreatePOD = async () => {
    if (!newPOD.delivery_number || !newPOD.recipient_name) {
      toast.error('Please fill in delivery number and recipient name');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/pod/pods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPOD)
      });

      if (response.ok) {
        toast.success('POD captured successfully');
        setShowCreateDialog(false);
        fetchPODs();
        setNewPOD({
          delivery_number: '',
          recipient_name: '',
          delivery_timestamp: new Date().toISOString(),
          delivery_notes: '',
          captured_by: 'USER'
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create POD');
      }
    } catch (error) {
      toast.error('Error creating POD');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Captured': 'bg-green-100 text-green-800',
      'Exception': 'bg-red-100 text-red-800',
      'Pending': 'bg-yellow-100 text-yellow-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Proof of Delivery</h1>
            <p className="text-gray-600">Capture and manage delivery confirmations</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <FileCheck className="w-4 h-4 mr-2" />
                Capture POD
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Capture Proof of Delivery</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Number *</label>
                  <Input
                    value={newPOD.delivery_number}
                    onChange={(e) => setNewPOD({...newPOD, delivery_number: e.target.value})}
                    placeholder="Enter delivery number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Recipient Name *</label>
                  <Input
                    value={newPOD.recipient_name}
                    onChange={(e) => setNewPOD({...newPOD, recipient_name: e.target.value})}
                    placeholder="Enter recipient name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Notes</label>
                  <Input
                    value={newPOD.delivery_notes}
                    onChange={(e) => setNewPOD({...newPOD, delivery_notes: e.target.value})}
                    placeholder="Optional delivery notes"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreatePOD}>Capture POD</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by delivery or POD ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* PODs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>POD ID</TableHead>
                <TableHead>Delivery Number</TableHead>
                <TableHead>Recipient Name</TableHead>
                <TableHead>Delivery Timestamp</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Captured By</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No PODs found. Click "Capture POD" to add delivery confirmations.
                  </TableCell>
                </TableRow>
              ) : (
                filteredPods.map((pod) => (
                  <TableRow key={pod.pod_id}>
                    <TableCell className="font-medium">{pod.pod_id}</TableCell>
                    <TableCell>{pod.delivery_number}</TableCell>
                    <TableCell>{pod.recipient_name}</TableCell>
                    <TableCell>{new Date(pod.delivery_timestamp).toLocaleString()}</TableCell>
                    <TableCell className="max-w-xs truncate">{pod.delivery_notes || '-'}</TableCell>
                    <TableCell>{pod.captured_by}</TableCell>
                    <TableCell>{getStatusBadge(pod.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredPods.length} of {pods.length} PODs
        </div>
      </div>
    </Layout>
  );
};

export default PODPage;